import { logError } from './monitorClient'

/**
 * Monitors IndexedDB for failures that could affect Firebase Auth persistence
 * and prompts user to reload the page when issues are detected
 */
export class IndexedDBMonitor {
  private static instance: IndexedDBMonitor
  private hasShownReloadPrompt = false
  private errorCount = 0
  private readonly MAX_ERRORS_BEFORE_PROMPT = 3
  private readonly ERROR_RESET_INTERVAL = 30000 // 30 seconds

  private constructor() {
    this.setupIndexedDBMonitoring()
    this.setupFirebaseAuthErrorHandling()
  }

  public static getInstance(): IndexedDBMonitor {
    if (!IndexedDBMonitor.instance) {
      IndexedDBMonitor.instance = new IndexedDBMonitor()
    }
    return IndexedDBMonitor.instance
  }

  private setupIndexedDBMonitoring(): void {
    // Monitor IndexedDB availability
    if (!window.indexedDB) {
      this.handleIndexedDBFailure('IndexedDB not supported')
      return
    }

    // Test IndexedDB functionality
    this.testIndexedDB()
  }

  private testIndexedDB(): void {
    try {
      const request = window.indexedDB.open('firebase-auth-test', 1)

      request.onerror = (event: Event) => {
        const target = event.target as IDBRequest<IDBDatabase>
        this.handleIndexedDBFailure('IndexedDB open error', target.error)
      }

      request.onsuccess = (event: Event) => {
        const target = event.target as IDBRequest<IDBDatabase>
        const db = target.result
        if (db) {
          db.close()
        }
      }

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const target = event.target as IDBOpenDBRequest
        const db = target.result
        if (db) {
          // Create a test object store
          if (!db.objectStoreNames.contains('test')) {
            db.createObjectStore('test', { keyPath: 'id' })
          }
        }
      }
    } catch (error) {
      this.handleIndexedDBFailure('IndexedDB test failed', error)
    }
  }

  private setupFirebaseAuthErrorHandling(): void {
    // Listen for storage events that might indicate IndexedDB issues
    window.addEventListener('storage', event => {
      if (event.key?.includes('firebase') || event.key?.includes('auth')) {
        this.handleIndexedDBFailure('Firebase storage event error')
      }
    })

    // Monitor for unhandled promise rejections that might be related to IndexedDB
    window.addEventListener('unhandledrejection', event => {
      const error = event.reason
      if (this.isIndexedDBRelatedError(error)) {
        this.handleIndexedDBFailure('Unhandled promise rejection related to IndexedDB', error)
      }
    })
  }

  private isIndexedDBRelatedError(error: unknown): boolean {
    if (!error) return false

    const errorMessage = (error as Error)?.message || String(error) || ''
    const errorName = (error as Error)?.name || ''

    return (
      errorMessage.includes('IndexedDB') ||
      errorMessage.includes('QuotaExceededError') ||
      errorMessage.includes('InvalidStateError') ||
      errorMessage.includes('firebase') ||
      errorMessage.includes('auth') ||
      errorName.includes('QuotaExceededError') ||
      errorName.includes('InvalidStateError')
    )
  }

  private handleIndexedDBFailure(message: string, error?: unknown): void {
    this.errorCount++

    logError(`IndexedDB failure detected: ${message}`, {
      error,
      errorCount: this.errorCount,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    })

    // Reset error count after interval
    setTimeout(() => {
      this.errorCount = Math.max(0, this.errorCount - 1)
    }, this.ERROR_RESET_INTERVAL)

    // Show reload prompt if we've hit the threshold and haven't shown it yet
    if (this.errorCount >= this.MAX_ERRORS_BEFORE_PROMPT && !this.hasShownReloadPrompt) {
      this.showReloadPrompt()
    }
  }

  private showReloadPrompt(): void {
    this.hasShownReloadPrompt = true

    // Use window.alert to prompt user to reload
    const userConfirmed = window.confirm(
      '⚠️ Session Issue Detected\n\n' +
        'We detected an issue with your session that may affect your experience. ' +
        'Please reload the page to ensure everything works properly.\n\n' +
        'Click OK to reload the page now, or Cancel to dismiss this warning.'
    )

    if (userConfirmed) {
      window.location.reload()
    } else {
      // Reset the flag after a delay to allow showing again if needed
      setTimeout(() => {
        this.hasShownReloadPrompt = false
      }, 60000) // 1 minute
    }
  }

  /**
   * Manually trigger IndexedDB failure handling (useful for testing)
   */
  public triggerFailure(message = 'Manual trigger'): void {
    this.handleIndexedDBFailure(message)
  }

  /**
   * Reset the monitor state (useful for testing)
   */
  public reset(): void {
    this.hasShownReloadPrompt = false
    this.errorCount = 0
  }

  /**
   * Directly show the reload prompt (bypasses error threshold, useful for testing)
   */
  public showReloadPromptDirectly(): void {
    this.showReloadPrompt()
  }
}

// Initialize the monitor when the module is loaded
const indexedDBMonitor = IndexedDBMonitor.getInstance()

export default indexedDBMonitor
